// src/components/DQRulesImpactTab.tsx
import React, { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import { Button, ButtonGroup, OverlayTrigger, Tooltip, Form, Card } from "react-bootstrap";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

interface ValidationResult {
  rule_id: string;
  status?: string;
  lifecycle?: string;
  dimension?: string;
  fail_count?: number;
  passed_count?: number;
  total_records?: number;
  sample_failures?: any[];
  examples?: {
    affected_count?: number;
    sample_changes?: any[];
  } | any;
  [k: string]: any;
}

interface Rule {
  id?: string;
  rule_id?: string;
  cat?: string;
  act?: string;
  sev?: string;
  d?: string;
  fix?: string;
  lifecycle?: string;
  [k: string]: any;
}

interface Insight {
  validation_rules?: Rule[];
  validation_results?: ValidationResult[];
}

interface DQRulesImpactTabProps {
  insight?: Insight;
}

const CAT_LABELS: Record<string, string> = {
  std: "Standardization",
  val: "Validation",
  cons: "Consistency",
  ref: "Reference",
  mgmt: "Governance",
};

const percent1 = (v: number) => parseFloat(v.toFixed(1));
const getRuleId = (r: Rule) => r.id || r.rule_id || "";
const safeRender = (v: any) => {
  if (v === null || v === undefined) return "-";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};

const DQRulesImpactTab: React.FC<DQRulesImpactTabProps> = ({ insight }) => {
  const rules: Rule[] = insight?.validation_rules || [];
  const validationResults: ValidationResult[] = insight?.validation_results || [];

  const [view, setView] = useState<"dimension" | "category">("dimension");
  const [selectedGroup, setSelectedGroup] = useState<{ key: string; metric: string } | null>(null);
  const [lifecycleFilter, setLifecycleFilter] = useState<"approved" | "all" | "non_approved">("approved");

  const normalizedRules = useMemo(() => rules || [], [rules]);

  const approvedRules = useMemo(() => {
    if (lifecycleFilter === "all") return normalizedRules;
    if (lifecycleFilter === "non_approved") {
      return normalizedRules.filter((r) => ((r.lifecycle || "").toLowerCase() || "draft") !== "approved");
    }
    return normalizedRules.filter((r) => (r.lifecycle || "").toLowerCase() === "approved");
  }, [normalizedRules, lifecycleFilter]);

  const computeRuleMetrics = (rule: Rule) => {
    const id = getRuleId(rule);
    const vr = validationResults.find((v) => v.rule_id === id);
    if (!vr) return null;

    const total = vr.total_records ?? 0;
    const passed = vr.passed_count ?? 0;
    const fail = vr.fail_count ?? 0;

    if ((rule.cat || "").toLowerCase() === "std") {
      const applied = (vr.examples && (vr.examples.affected_count ?? vr.examples?.applied_count ?? 0)) || 0;
      return { rule, vr, passRate: 100.0, passRateRaw: 100.0, total, passed, fail, applied };
    }

    const passRateRaw = total > 0 ? (passed / total) * 100 : 100;
    const passRate = percent1(passRateRaw);
    return {
      rule,
      vr,
      passRate,
      passRateRaw,
      total,
      passed,
      fail,
      applied: (vr.examples && (vr.examples.affected_count ?? 0)) || 0,
    };
  };

  const approvedRuleMetrics = useMemo(() => {
    return approvedRules
      .map((r) => computeRuleMetrics(r))
      .filter((m): m is Exclude<typeof m, null> => m !== null);
  }, [approvedRules, validationResults]);

  const rollups = useMemo(() => {
    const buckets: Record<
      string,
      { passRates: number[]; totalRecords: number; weightedSum: number; appliedCount: number; ruleMetrics: any[] }
    > = {};

    approvedRuleMetrics.forEach((m) => {
      const rule = m.rule;
      const vr = m.vr;
      const key =
        view === "dimension"
          ? rule.dq_dim || vr.dimension || "Other"
          : CAT_LABELS[(rule.cat as string) || ""] || rule.cat || "Other";

      if (!buckets[key]) buckets[key] = { passRates: [], totalRecords: 0, weightedSum: 0, appliedCount: 0, ruleMetrics: [] };

      if ((rule.cat || "").toLowerCase() === "std") {
        buckets[key].appliedCount += m.applied ?? 0;
      } else {
        buckets[key].passRates.push(m.passRate);
        buckets[key].weightedSum += (m.passRateRaw ?? m.passRate) * (m.total || 0);
        buckets[key].totalRecords += m.total || 0;
      }

      buckets[key].ruleMetrics.push(m);
    });

    const out: Record<string, { avg: number; worst: number; weighted: number; applied: number; count: number }> = {};
    Object.entries(buckets).forEach(([k, v]) => {
      const avg = v.passRates.length ? v.passRates.reduce((s, x) => s + x, 0) / v.passRates.length : 0;
      const worst = v.passRates.length ? Math.min(...v.passRates) : 0;
      const weighted = v.totalRecords > 0 ? v.weightedSum / v.totalRecords : avg;
      out[k] = {
        avg: percent1(avg),
        worst: percent1(worst),
        weighted: percent1(weighted),
        applied: v.appliedCount,
        count: v.ruleMetrics.length,
      };
    });
    return out;
  }, [approvedRuleMetrics, view]);

  const labels = Object.keys(rollups);

  const filteredRules = useMemo(() => {
    if (!selectedGroup) return [];
    const key = selectedGroup.key;
    const groupMetrics = approvedRuleMetrics.filter((m) => {
      const rule = m.rule;
      const vr = m.vr;
      const groupKey =
        view === "dimension"
          ? rule.dq_dim || vr.dimension || "Other"
          : CAT_LABELS[(rule.cat as string) || ""] || rule.cat || "Other";
      return groupKey === key;
    });
    return groupMetrics;
  }, [selectedGroup, approvedRuleMetrics, view]);

  const nonApprovedCount = (rules || []).filter((r) => (r.lifecycle || "").toLowerCase() !== "approved").length;
  const totalRules = approvedRuleMetrics.length;
  const totalFails = approvedRuleMetrics.reduce((s, m) => s + (m.fail || 0), 0);
  const totalPassed = approvedRuleMetrics.reduce((s, m) => s + (m.passed || 0), 0);
  const totalApplied = approvedRuleMetrics.reduce((s, m) => s + (m.applied || 0), 0);
  const totalRecords = approvedRuleMetrics.reduce((s, m) => s + (m.total || 0), 0);
  const overallPassRate = totalRecords > 0 ? ((totalPassed / totalRecords) * 100).toFixed(1) : "100.0";

  return (
    <div>
      {/* Header & Lifecycle Filter */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <h5 className="mb-0">📈 Data Quality Summary</h5>
          <OverlayTrigger placement="right" overlay={<Tooltip id="tip-non-approved">{nonApprovedCount} non-approved rules (Draft / Proposed)</Tooltip>}>
            <span className="badge bg-secondary" style={{ cursor: "help" }}>+{nonApprovedCount} non-approved</span>
          </OverlayTrigger>
        </div>
        <Form.Select size="sm" value={lifecycleFilter} onChange={(e) => { setLifecycleFilter(e.target.value as any); setSelectedGroup(null); }} style={{ width: 220 }}>
          <option value="approved">Approved rules only</option>
          <option value="all">All lifecycles</option>
          <option value="non_approved">Non-approved only</option>
        </Form.Select>
      </div>

      {/* Summary Cards */}
      <div className="d-flex flex-wrap gap-3 mb-4">
        {[{ label: "Approved Rules", value: totalRules },
          { label: "Overall Pass Rate", value: `${overallPassRate}%` },
          { label: "Total Failures", value: totalFails },
          { label: "Standardizations Applied", value: totalApplied }].map((s, i) => (
          <div key={i} className="p-3 border rounded bg-white shadow-sm" style={{ flex: "1 1 200px", textAlign: "center" }}>
            <div className="text-muted">{s.label}</div>
            <div style={{ fontWeight: 700, fontSize: 20 }}>{safeRender(s.value)}</div>
          </div>
        ))}
      </div>

      {/* Group Summary Cards */}
      <div className="my-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <ButtonGroup>
            <Button variant={view === "dimension" ? "primary" : "outline-primary"} onClick={() => { setView("dimension"); setSelectedGroup(null); }}>
              By Dimension
            </Button>
            <Button variant={view === "category" ? "primary" : "outline-primary"} onClick={() => { setView("category"); setSelectedGroup(null); }}>
              By Category
            </Button>
          </ButtonGroup>
        </div>

        <div className="d-flex flex-wrap gap-3">
          {Object.entries(rollups).map(([k, v]) => {
            const isSelected = selectedGroup?.key === k;
            return (
              <div
                key={k}
                onClick={() => setSelectedGroup({ key: k, metric: "Avg" })}
                className={`p-3 border rounded shadow-sm ${isSelected ? "bg-primary text-white" : "bg-white"}`}
                style={{
                  width: 220,
                  cursor: "pointer",
                  transition: "0.2s",
                  boxShadow: isSelected ? "0 0 0 2px #0d6efd" : "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                <div style={{ fontSize: 13, opacity: 0.8 }}>{safeRender(k)}</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{safeRender(v.avg)}%</div>
                <div style={{ fontSize: 12, opacity: 0.8 }}>{v.count} rules • Worst {safeRender(v.worst)}%</div>
                {v.applied > 0 && <div style={{ fontSize: 12 }}>{v.applied} standardized</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Drill-down Section */}
      {selectedGroup && filteredRules.length > 0 && (
        <div>
          <h5 className="mb-3">
            Drill-down: <strong>{safeRender(selectedGroup.key)}</strong> ({filteredRules.length} rules)
          </h5>
          {filteredRules.map(({ rule, vr, passRate, applied }) => {
            const total = vr.total_records ?? 0;
            return (
              <div key={getRuleId(rule)} className="mb-3 p-3 border rounded bg-light">
                <div className="d-flex justify-content-between mb-1">
                  <div>
                    <strong>{safeRender(getRuleId(rule))}</strong> – {safeRender(rule.d || "No description")}
                    <div className="small text-muted">{safeRender(rule.lifecycle || "Unknown")}</div>
                  </div>
                  <div style={{ fontWeight: 700, color: "#0d6efd" }}>
                    {(rule.cat || "").toLowerCase() === "std" ? `${applied} applied` : `${passRate}% pass`}
                  </div>
                </div>

                {/* Summary line */}
                <div className="small text-muted mb-2">
                  {vr.fail_count ?? 0} failed / {vr.passed_count ?? 0} passed / {total} total
                </div>

                {/* Examples Section */}
                {((rule.cat || "").toLowerCase() === "std" && vr.examples) && (
                  <details className="mt-2">
                    <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                      🧩 Examples / Applied ({vr.examples.affected_count ?? 0})
                    </summary>
                    <div className="mt-2 p-2 bg-white rounded border small">
                      {Array.isArray(vr.examples.sample_changes) && vr.examples.sample_changes.length > 0 ? (
                        vr.examples.sample_changes.slice(0, 5).map((ch: any, i: number) => (
                          <Card
                            key={i}
                            className="p-2 mt-2 bg-light"
                            style={{ fontSize: "0.85rem" }}
                          >
                            {Object.entries(ch.field_changes as Record<string, { before: any; after: any }>).map(
                            ([fname, diff]) => {
                              const beforeVal = (diff as any)?.before ?? "";
                              const afterVal = (diff as any)?.after ?? "";
                              return (
                                <div key={fname}>
                                  <b>{"Record Index:"} {ch.index}</b>:{" "}
                                  <span className="text-danger">{String(beforeVal)}</span> →{" "}
                                  <span className="text-success">{String(afterVal)}</span>
                                </div>
                              );
                            })}

                          </Card>
                        ))
                      ) : (
                        <pre style={{ whiteSpace: "pre-wrap" }}>{safeRender(vr.examples)}</pre>
                      )}
                      {vr.examples.sample_changes?.length > 5 && (
                        <div className="text-muted mt-1">... and {vr.examples.sample_changes.length - 5} more</div>
                      )}
                    </div>
                  </details>
                )}

                {/* Failures Section */}
                {Array.isArray(vr.sample_failures) && vr.sample_failures.length > 0 && (
                  <details className="mt-2">
                    <summary style={{ cursor: "pointer", fontWeight: 600 }}>
                      🔍 Sample Failures ({vr.sample_failures.length})
                    </summary>
                    <div className="mt-2 small">
                      {vr.sample_failures.slice(0, 3).map((f, i) => (
                        <div key={i} className="border rounded p-2 mb-1 bg-white">
                          {f.failure_reason && <div><strong>Reason:</strong> {safeRender(f.failure_reason)}</div>}
                          {f.value_checked && <div><strong>Value Checked:</strong> {safeRender(f.value_checked)}</div>}
                          {f.record && (
                            <details>
                              <summary className="text-muted">View record JSON</summary>
                              <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>{safeRender(f.record)}</pre>
                            </details>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DQRulesImpactTab;
